/*
 * Copyright (c) 2023. Bolmate B.V.
 */
import './style/ratingExports.scss';

import {AwesomeIcon, Link, Loader} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import classNames from 'classnames';
import React, {CSSProperties, MouseEventHandler, useMemo} from 'react';
import ProcessStatus from "../../../models/processStatusModel";
import {BolmateButton} from "../../controls";
import {AccessKey, ProcessingStatus} from "../../../util/interfaces";
import {Rating} from "../../../util/types";
import useRight from "../../misc/useRight";

interface Props {
    onCreateExport: MouseEventHandler;
    onDownloadExport: (process: ProcessStatus) => MouseEventHandler;
    onDeleteExport: (process: ProcessStatus) => MouseEventHandler;
    onCloseProcessInfo: MouseEventHandler;
    exports: Array<ProcessStatus>;
    loading: boolean;
    className?: string;
    ean?: string;
    showProcessInfo: boolean;
}

function getDateValue(process: ProcessStatus, dateKey: 'from_date' | 'to_date'): IDate | null {
    const dateStr = process.getData(dateKey);
    if (!dateStr) return null;
    return new IDate(dateStr);
}

export default function RatingExports(props: Props) {
    const [canCreate, canDownload] = useRight(AccessKey.RATING_CREATE_EXPORT, AccessKey.RATING_DOWNLOAD_EXPORT);
    const {
        exports, onCreateExport, onDownloadExport, className, loading, ean, onDeleteExport, showProcessInfo,
        onCloseProcessInfo
    } = props;
    const rowStyle: CSSProperties = useMemo(() => {
        const templateColumns = ['8rem', '8rem', '8rem', '12rem', '1fr', '1rem']
        if (!ean) templateColumns.splice(1, 0, '10rem');
        return {
            gridTemplateColumns: templateColumns.join(' '),
        }
    }, [ean]);
    const hasActiveExports = useMemo(() => {
        return !!exports.find((ratingExport: ProcessStatus) => ratingExport.status === ProcessingStatus.RUNNING);
    }, [exports]);

    function renderExport(process: ProcessStatus, index: number) {
        const dateFormat = 'DD-MM-YYYY';
        const selectedRatings: Array<number> | undefined = process.getData('only_ratings');
        const allSelected = !!selectedRatings && ![1, 2, 3, 4, 5].find((rating: Rating) => !selectedRatings.includes(rating));
        return (
            <div key={index} className='row default-table-row' style={rowStyle}>
                <label>{process.createdAtMoment.format(dateFormat)}</label>
                {!ean && <label>{process.getData('ean') ?? '-'}</label>}
                <label>{getDateValue(process, 'from_date')?.format(dateFormat) ?? '-'}</label>
                <label>{getDateValue(process, 'to_date')?.format(dateFormat) ?? '-'}</label>
                <label>{allSelected ? 'Alle scores' : selectedRatings?.join(', ') ?? '-'}</label>
                {renderStatus(process)}
                <AwesomeIcon name='trash-alt' onClick={canCreate ? onDeleteExport(process) : undefined}
                             className='delete-btn'/>
            </div>
        )
    }

    function renderStatus(process: ProcessStatus) {
        if (process.status === ProcessingStatus.FINISHED) {
            if (!!process.getData('file_id'))
                return <Link
                    onClick={canDownload ? onDownloadExport(process) : undefined}>Gereed{canDownload ? ' - export downloaden' : ''}</Link>
            else
                return <label>{process.getData('result')}</label>;
        } else if (process.status === ProcessingStatus.ERROR)
            return <label className='error-status'>{process.getData('result')}</label>
        return <label>Reviews worden geëxporteerd...</label>;
    }

    return (
        <div className={classNames('rating-exports', className)}>
            <div className='action-bar'>
                <BolmateButton onClick={onCreateExport} label='Nieuwe export maken' disabled={loading || !canCreate}/>
            </div>
            {(showProcessInfo && hasActiveExports) && (
                <div className='process-info'>
                    <p className='message'>Het exporteren van de reviews is gestart. Afhankelijk van het aantal reviews
                        kan dit even duren, je kunt de pagina in de tussentijd verlaten.</p>
                    <span className='close-btn' onClick={onCloseProcessInfo}>Oké</span>
                </div>
            )}
            <div className='exports-container'>
                <Loader active={loading}/>
                <div className='row default-table-row head' style={rowStyle}>
                    <label>AANGEMAAKT OP</label>
                    {!ean && <label>EAN</label>}
                    <label>VANAF DATUM</label>
                    <label>TOT DATUM</label>
                    <label>GESELECTEERDE SCORES</label>
                    <label>STATUS</label>
                    <div/>
                </div>
                {exports.map(renderExport)}
                {!exports.length && <div className='default-table-row empty'>
                    <label className='empty-label'>- Je hebt nog geen exports gemaakt -</label>
                </div>}
            </div>
        </div>
    )
}